import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Math as MathFormula } from "@/components/Math";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function NewtonsLawCalculator() {
  const [mass, setMass] = useState("");
  const [acceleration, setAcceleration] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateForce = () => {
    const m = parseFloat(mass);
    const a = parseFloat(acceleration);

    if (isNaN(m) || isNaN(a)) {
      toast.error("Please enter valid values");
      return;
    }

    const force = m * a;
    setResult(force);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setMass("");
    setAcceleration("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Newton's Law Calculator" 
      calculatorPath="/calculator/newtons-law"
      category="physics"
      subcategory="Mechanics"
    >
      <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Formula: F = m × a</CardTitle>
              <CardDescription>
                Where F is force (N), m is mass (kg), and a is acceleration (m/s²)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mass">Mass (kg)</Label>
                  <Input
                    id="mass"
                    type="number"
                    step="0.01"
                    placeholder="Enter mass"
                    value={mass}
                    onChange={(e) => setMass(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="acceleration">Acceleration (m/s²)</Label>
                  <Input
                    id="acceleration"
                    type="number"
                    step="0.01"
                    placeholder="Enter acceleration"
                    value={acceleration}
                    onChange={(e) => setAcceleration(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={calculateForce} className="flex-1">
                  <Calculator className="mr-2 h-4 w-4" />
                  Calculate
                </Button>
                <Button onClick={reset} variant="outline">
                  Reset
                </Button>
              </div>

              {result !== null && (
                <>
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Result</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {result.toFixed(2)} N
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Force (Newtons)
                      </p>
                    </CardContent>
                  </Card>
                  
                  <CalculatorSuperCalcCTA config={calculatorCTAs["newtons-law"]} trigger={result} />
                </>
              )}

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-sm">Formula</CardTitle>
                </CardHeader>
                <CardContent>
                  <MathFormula block>{String.raw`F = ma`}</MathFormula>
                  <p className="text-sm text-muted-foreground mt-2">
                    Force (N) = mass (kg) × acceleration (m/s²)
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">How to use</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>1. Enter the mass of the object in kilograms</p>
                  <p>2. Enter the acceleration in meters per second squared</p>
                  <p>3. Click Calculate to get the force in Newtons</p>
                  <p className="pt-2 font-medium text-foreground">
                    Example: 10 kg mass with 5 m/s² acceleration = 50 N force
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
    </PageLayout>
  );
}
